import React, { useEffect, useState } from 'react';
import { Topbar, Alert } from '@components';
import { useTranslation } from 'react-i18next';
import { PUBLIC_ROUTE, PRIVATE_ROUTE } from "@router/route.constants";
import { useNavigate } from "react-router-dom";
import {
  RootContainer,
  Form,
  Input,
  TextArea,
  Select,
  ButtonContainer,
  Button,
  PendingContainer,
  PendingTitle,
  PendingDesc,
  BottomText,
  Loader,
} from './GameLostRequest.style';
import { api } from '@api'
import { useSelector, useDispatch } from 'react-redux';
import { CHINESE_LANGUAGE_CODE_LK, BURMESE_LANGUAGE_CODE_LK } from '@constants'
import { openAlert } from "@redux/app/appSlice";
import { Spin } from "antd";
import { RequestPendingLoading, SelectRightIcon, GameRequestHistoryIcon } from '@assets';
import Lottie from 'react-lottie';
import { localStorage } from '@utils';

const GameLostRequest = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [gameList, setGameList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enableRequest, setEnableRequest] = useState(false);
  const [lossRequestList, setLossRequestList] = useState(false);
  const [showPendingUi, setShowPendingUi] = useState(false);
  const lang = useSelector((state) => state.app.userPreferedLanguage);
  const [isSubmitting, setIsSubmitting] = useState();
  const [gameId, setGameId] = useState('')
  const userPreferedLanguage = localStorage.getUserPreferedLanguage();

  let nameKey =
    lang === CHINESE_LANGUAGE_CODE_LK
      ? "name_ch"
      : lang === BURMESE_LANGUAGE_CODE_LK
        ? "name_mm"
        : "name_en";

  useEffect(() => {
    Promise.all([checkGameLossRequests(), getGameList()])
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error in API calls", error);
        setLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  const checkGameLossRequests = () => new Promise((resolve, reject) => {
    api
      .checkGameLossRequests()
      .then(({ enable_request, loss_requests }) => {
        setEnableRequest(enable_request);
        setLossRequestList(loss_requests);
        if (!enable_request) {
          if (loss_requests.length === 3) {
            showAlert({
              message: `${t("profile.requestLimitReachMessage")}`,
              isError: true
            })
          } else {
            showAlert({
              message: `${t("profile.disableRequestMessage")}`,
              isError: true
            })
            setShowPendingUi(true);
          }
        }
        resolve();
      })
      .catch((errorMsg) => {
        showAlert({
          message: `${t("share.viewErrorMsg")}`,
          isError: true,
          successCallback: goBack,
        });
        reject();
      })
  });

  const getGameList = () => new Promise((resolve, reject) => {
    api
      .getGameList()
      .then(({ games }) => {
        setGameList(games);
        resolve();
      })
      .catch((errorMsg) => {
        showAlert({
          message: `${t("share.viewErrorMsg")}`,
          isError: true,
          successCallback: goBack,
        });
        reject();
      })
  });

  const goToProfile = () => {
    navigate(`/${PUBLIC_ROUTE.PROFILE}`);
  };

  const goToRequestHistory = () => {
    navigate(`/${PRIVATE_ROUTE.GAME_LOST_REQUEST_HISTORY}`)
  }

  const goBack = () => {
    goToProfile();
  }

  const handleCancel = () => {
    goToProfile();
  }

  const showAlert = (data) => {
    dispatch(openAlert(data));
  };

  //return true if provide data found in previous loss request, else retur false
  const validateRequest = (data) => {
    return lossRequestList.some(req => {
      return req.child_game_id === data.child_game_id && req.request_amount === Number(data.request_amount);
    });
  };

  const onFinish = (data) => {
    setIsSubmitting(true);
    const isAlreadyRequest = validateRequest(data);

    if (isAlreadyRequest) {
      showAlert({
        message: `${t("profile.pendingRequestMessage")}`,
        isError: true
      })
      setIsSubmitting(false)
      return;
    }

    api
      .requestBalanceLoss({
        child_game_id: data.child_game_id,
        request_amount: Number(data.request_amount),
        note: data.note
      })
      .then(() => {
        showAlert({
          message: `${t("profile.requestSuccessMessage")}`,
          successCallback: setShowPendingUi(true),
        })
        setIsSubmitting(false);
      })
      .catch((errorMsg) => {
        showAlert({
          message: errorMsg,
          isError: true,
        });
        setIsSubmitting(false);
      })
  }

  const filterOption = (input, option) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  return (
    <>
      <Topbar
        title={`${t("profile.gameLostRequest")}`}
        goBack={goBack}
        rightIcon={<GameRequestHistoryIcon onClick={goToRequestHistory} />}
      />
      <RootContainer>

        {loading && (
          <Loader>
            <Spin />
          </Loader>
        )}

        {!loading && !showPendingUi && (
          <Form
            layout='vertical'
            onFinish={onFinish}
            requiredMark={false}
          >

            <Form.Item
              label={`${t("profile.latestPlayGame")}(${t("profile.requirement")})`}
              name="child_game_id"
              rules={[
                {
                  required: true,
                  message: ''
                }
              ]}
            >
              <Select
                isFilled={gameId.length > 0 ? "true" : "false"}
                suffixIcon={<SelectRightIcon />}
                showSearch
                onChange={value => setGameId(value)}
                placeholder={t('profile.selectPlaceholder')}
                filterOption={filterOption}
                options={
                  gameList.map(game => ({
                    value: game.child_game_id,
                    label: game[nameKey]
                  })
                  )
                }
              />
            </Form.Item>
            <Form.Item
              label={`${t("profile.latestAmount")}(${t("profile.requirement")})`}
              name='request_amount'
              rules={[
                {
                  required: true,
                  message: ''
                }
              ]}
            >
              <Input
                type='number'
                placeholder='Eg:20,000'
              />
            </Form.Item>

            <Form.Item
              label={`${t('profile.note')}(${t("profile.optional")})`}
              name='note'
            >
              <TextArea
                style={{ background: "#E5E0F5", textAlign: 'left' }}
                type='text'
                placeholder={t("profile.noteLabel")}
                rows={6}
              />
            </Form.Item>
            <ButtonContainer>
              <Button
                type='cancel'
                onClick={handleCancel}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type='ok'
                htmlType="Submit"
                disabled={!enableRequest}
                loading={isSubmitting}
              >
                {t("common.ok")}
              </Button>
            </ButtonContainer>
          </Form>
        )}

        {showPendingUi && !loading && (
          <PendingContainer>
            <Lottie
              options={{
                animationData: RequestPendingLoading
              }}
              height={108}
              style={{ margin: '40px 0' }}
            />
            <PendingTitle> {t("profile.pendingRequestTitle")} </PendingTitle>
            <PendingDesc > {t("profile.pendingRequestDesc")} </PendingDesc>
            <BottomText isBurmese={userPreferedLanguage === BURMESE_LANGUAGE_CODE_LK ? "true" : "false"}> {t("profile.pendingFooter")} </BottomText>
          </PendingContainer>
        )}

      </RootContainer>
      <Alert />
    </>
  )
}

export default GameLostRequest